import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  ReceiptText,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { hasCapability } from '../../../permissions';
import { useAppToast } from '../../../components/AppToast';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { ErrorState } from '../../shared/components/ErrorState';
import { ModuleLoadingState, ModulePageHeader } from '../../shared/components/ModulePageHeader';
import { normalizeOrganizationStatus } from '../utils/normalizeOrganizationStatus';
import { openCustomerPortal } from '../../../services/paymentService';
import type { PlanSlug } from '../../../types/saasTypes';

interface BillingSnapshot {
  plan: string | null;
  planSlug: string | null;
  monthlyValue: number;
  mrr: number;
  nextBilling: string | null;
  status: string | null;
}

interface PlanFeature {
  label: string;
  included: boolean;
}

interface PlanDef {
  id: PlanSlug;
  name: string;
  value: number;
  badge?: string;
  icon: React.ElementType;
  description: string;
  features: PlanFeature[];
  cta: string;
  highlighted: boolean;
}

const PLAN_DEFINITIONS: PlanDef[] = [
  {
    id: 'free',
    name: 'Free',
    value: 0,
    icon: Zap,
    description: 'Para times pequenos explorando a plataforma.',
    highlighted: false,
    cta: 'Utilizar Free',
    features: [
      { label: '1 usuário', included: true },
      { label: '1 projeto ativo', included: true },
      { label: 'Processos básicos', included: true },
      { label: 'Monitor de execuções', included: false },
      { label: 'Padrões e modelos', included: false },
      { label: 'Relatórios avançados', included: false },
      { label: 'Suporte por e-mail', included: false },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    value: 497,
    icon: BadgeCheck,
    description: 'Para equipes em crescimento com operações estruturadas.',
    highlighted: false,
    cta: 'Assinar Starter',
    features: [
      { label: 'Até 5 usuários', included: true },
      { label: '10 projetos ativos', included: true },
      { label: 'Processos avançados', included: true },
      { label: 'Monitor de execuções', included: true },
      { label: 'Padrões e modelos', included: true },
      { label: 'Relatórios avançados', included: false },
      { label: 'Suporte por e-mail', included: true },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    value: 1490,
    badge: 'Recomendado',
    icon: Sparkles,
    description: 'Para organizações que exigem máxima capacidade operacional.',
    highlighted: true,
    cta: 'Assinar Pro',
    features: [
      { label: 'Usuários ilimitados', included: true },
      { label: 'Projetos ilimitados', included: true },
      { label: 'Todas as funcionalidades', included: true },
      { label: 'Monitor de execuções', included: true },
      { label: 'Padrões e modelos', included: true },
      { label: 'Relatórios avançados', included: true },
      { label: 'Suporte prioritário', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    value: 0,
    icon: Building2,
    description: 'Capacidade dedicada, SLA garantido e consultoria especializada.',
    highlighted: false,
    cta: 'Falar com especialista',
    features: [
      { label: 'Tudo do Pro', included: true },
      { label: 'SLA dedicado', included: true },
      { label: 'Consultoria Lean inclusa', included: true },
      { label: 'Integrações customizadas', included: true },
      { label: 'Treinamento de equipe', included: true },
      { label: 'Gerente de conta', included: true },
      { label: 'Contrato personalizado', included: true },
    ],
  },
];

export const BillingPage: React.FC = () => {
  const { workspace } = useWorkspace();
  const { showToast, ToastContainer } = useAppToast();
  const navigate = useNavigate();
  const organizationId = workspace?.organizationId;
  const canManageBilling = hasCapability(
    workspace?.userRole ? { role: workspace.userRole } : null,
    'billing.manage',
    { realRoleOnly: true },
  );
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('pro');
  const [isProcessing, setIsProcessing] = useState(false);

  const billingQuery = useQuery({
    queryKey: ['billing-page', organizationId],
    enabled: Boolean(organizationId),
    queryFn: async (): Promise<BillingSnapshot | null> => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('status, amount, billing_cycle, current_period_end, plan_id, plans(name, slug)')
        .eq('workspace_id', organizationId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const monthlyValue = Number(data.amount ?? 0);
      const plan = (data.plans as { name: string; slug: string } | null);

      return {
        plan: plan?.name ?? null,
        planSlug: plan?.slug ?? null,
        monthlyValue,
        mrr: monthlyValue,
        nextBilling: data.current_period_end ?? null,
        status: data.status ?? null,
      };
    },
  });

  const currency = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }),
    [],
  );

  const normalizedStatus = normalizeOrganizationStatus({ status: billingQuery.data?.status });
  const normalizedStatusTone =
    normalizedStatus === 'ACTIVE'
      ? 'active'
      : normalizedStatus === 'TRIAL'
        ? 'warning'
        : normalizedStatus === 'SUSPENDED' || normalizedStatus === 'CANCELED'
          ? 'risk'
          : 'draft';

  const openPlanModal = () => {
    setSelectedPlan((billingQuery.data?.planSlug || billingQuery.data?.plan || 'pro').toLowerCase());
    setIsPlanModalOpen(true);
  };

  const handleSelectPlan = async (planId: PlanSlug) => {
    if (!workspace?.organizationId) return;
    if (planId === 'enterprise') {
      showToast('info', 'Entre em contato com nossa equipe para configurar o plano Enterprise.');
      return;
    }
    if (planId === 'free') {
      showToast('info', 'Você já está no plano Free.');
      return;
    }
    navigate(`/checkout?plan=${planId}&cycle=monthly`);
  };

  const confirmPlanChange = async () => {
    if (!workspace?.organizationId) return;
    if (selectedPlan === 'free') {
      showToast('info', 'Você já está no plano Free.');
      setIsPlanModalOpen(false);
      return;
    }
    if (selectedPlan === 'enterprise') {
      showToast('info', 'Entre em contato com nossa equipe para o plano Enterprise.');
      setIsPlanModalOpen(false);
      return;
    }
    setIsPlanModalOpen(false);
    navigate(`/checkout?plan=${selectedPlan}&cycle=monthly`);
  };

  const handleManageSubscription = async () => {
    if (!workspace?.organizationId) return;
    setIsProcessing(true);
    try {
      const result = await openCustomerPortal(workspace.organizationId);
      if (result.success && result.portalUrl) {
        window.location.href = result.portalUrl;
      } else {
        showToast('info', 'Portal de pagamento não disponível. Configure o Stripe para acesso completo.');
      }
    } catch {
      showToast('error', 'Erro ao abrir portal de assinatura.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <RequireCapability capability="billing.manage" realRoleOnly>
      <div className="enterprise-page h-full overflow-y-auto">
        <ModulePageHeader icon={CreditCard} title="Plano e Assinatura" subtitle="Gestão financeira e capacidade contratada" />

        {billingQuery.isPending && <ModuleLoadingState label="Carregando informações de billing" />}

        {billingQuery.isError && (
          <ErrorState message="Não foi possível carregar os dados de faturamento." onRetry={billingQuery.refetch} />
        )}

        {/* ── NO SUBSCRIPTION: show plan selection cards ── */}
        {!billingQuery.isPending && !billingQuery.isError && !billingQuery.data && (
          <PlanSelectionView
            canManageBilling={canManageBilling}
            isProcessing={isProcessing}
            currency={currency}
            onSelectPlan={handleSelectPlan}
          />
        )}

        {/* ── HAS SUBSCRIPTION: show billing dashboard ── */}
        {billingQuery.data && (
          <BillingDashboard
            data={billingQuery.data}
            canManageBilling={canManageBilling}
            isProcessing={isProcessing}
            currency={currency}
            normalizedStatus={normalizedStatus}
            normalizedStatusTone={normalizedStatusTone}
            orgName={workspace?.organizationName}
            onManageSubscription={handleManageSubscription}
            onChangePlan={openPlanModal}
          />
        )}

        {/* ── PLAN CHANGE MODAL ── */}
        {isPlanModalOpen && (
          <PlanChangeModal
            selectedPlan={selectedPlan}
            isProcessing={isProcessing}
            currency={currency}
            onSelectPlan={setSelectedPlan}
            onConfirm={confirmPlanChange}
            onClose={() => setIsPlanModalOpen(false)}
          />
        )}

        <ToastContainer />
      </div>
    </RequireCapability>
  );
};

// ============================================================================
// PLAN SELECTION (empty state)
// ============================================================================

const PlanSelectionView: React.FC<{
  canManageBilling: boolean;
  isProcessing: boolean;
  currency: Intl.NumberFormat;
  onSelectPlan: (plan: PlanSlug) => void;
}> = ({ canManageBilling, isProcessing, currency, onSelectPlan }) => (
  <div className="py-8 space-y-6">
    {/* Onboarding header */}
    <div className="enterprise-card-surface rounded px-5 py-4 flex items-start gap-4">
      <div className="shrink-0 w-9 h-9 rounded-md bg-[var(--brand-primary)]/10 flex items-center justify-center text-[var(--brand-primary)]">
        <ReceiptText size={18} />
      </div>
      <div>
        <p className="text-sm font-medium enterprise-text-primary">Nenhuma assinatura ativa</p>
        <p className="text-xs enterprise-text-secondary mt-0.5">
          Sua organização ainda não possui um plano registrado. Selecione um plano abaixo para ativar todas as funcionalidades.
        </p>
      </div>
    </div>

    {/* Plan cards grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {PLAN_DEFINITIONS.map((plan) => {
        const Icon = plan.icon;
        return (
          <div
            key={plan.id}
            className={`relative flex flex-col enterprise-card-surface rounded overflow-hidden ${
              plan.highlighted ? 'ring-2 ring-[var(--brand-primary)]' : ''
            }`}
          >
            {plan.badge && (
              <div className="absolute top-0 right-0 bg-[var(--brand-primary)] text-[var(--text-inverse)] text-[10px] font-semibold tracking-wider uppercase px-2.5 py-0.5 rounded-bl-md">
                {plan.badge}
              </div>
            )}

            {/* Plan header */}
            <div className={`px-4 pt-5 pb-4 border-b border-[var(--border-default)] ${plan.highlighted ? 'bg-[var(--brand-primary)]/5' : ''}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded flex items-center justify-center ${
                  plan.highlighted
                    ? 'bg-[var(--brand-primary)] text-[var(--text-inverse)]'
                    : 'bg-[var(--bg-surface-2)] enterprise-text-secondary'
                }`}>
                  <Icon size={14} />
                </div>
                <span className="text-sm font-semibold enterprise-text-primary">{plan.name}</span>
              </div>
              <div className="mb-2">
                {plan.value > 0 ? (
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold font-mono tabular-nums enterprise-text-primary">
                      {currency.format(plan.value)}
                    </span>
                    <span className="text-xs enterprise-text-muted">/mês</span>
                  </div>
                ) : plan.id === 'enterprise' ? (
                  <span className="text-xl font-semibold enterprise-text-primary">Sob consulta</span>
                ) : (
                  <span className="text-xl font-bold enterprise-text-primary">Grátis</span>
                )}
              </div>
              <p className="text-xs enterprise-text-secondary leading-relaxed">{plan.description}</p>
            </div>

            {/* Features */}
            <ul className="flex-1 px-4 py-3 space-y-1.5">
              {plan.features.map((feat) => (
                <li key={feat.label} className="flex items-center gap-2">
                  <CheckCircle2
                    size={13}
                    className={feat.included ? 'text-[var(--brand-primary)] shrink-0' : 'text-[var(--text-muted)] shrink-0'}
                  />
                  <span className={`text-xs ${feat.included ? 'enterprise-text-primary' : 'enterprise-text-muted line-through'}`}>
                    {feat.label}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            {canManageBilling && (
              <div className="px-4 pb-4 pt-3 border-t border-[var(--border-default)]">
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => onSelectPlan(plan.id)}
                  className={`w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold min-h-[var(--button-height)] rounded-[var(--radius-md)] px-3 transition-colors ${
                    plan.highlighted
                      ? 'enterprise-button-primary'
                      : 'enterprise-button-secondary'
                  }`}
                >
                  {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                  {plan.cta}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>

    <p className="text-center text-xs enterprise-text-muted">
      Todos os planos incluem acesso ao painel de controle e suporte inicial. Cancele quando quiser.
    </p>
  </div>
);

// ============================================================================
// BILLING DASHBOARD (has subscription)
// ============================================================================

const BillingDashboard: React.FC<{
  data: BillingSnapshot;
  canManageBilling: boolean;
  isProcessing: boolean;
  currency: Intl.NumberFormat;
  normalizedStatus: string;
  normalizedStatusTone: 'active' | 'warning' | 'risk' | 'draft';
  orgName?: string;
  onManageSubscription: () => void;
  onChangePlan: () => void;
}> = ({ data, canManageBilling, isProcessing, currency, normalizedStatus, normalizedStatusTone, orgName, onManageSubscription, onChangePlan }) => {
  const planDef = PLAN_DEFINITIONS.find((p) => p.id === (data.planSlug ?? data.plan?.toLowerCase()));
  const PlanIcon = planDef?.icon ?? CreditCard;

  return (
    <div className="space-y-4 py-4">
      {/* ── Status Banner ── */}
      <div className="enterprise-card-surface rounded px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-md bg-[var(--brand-primary)]/10 flex items-center justify-center text-[var(--brand-primary)] shrink-0">
            <PlanIcon size={20} />
          </div>
          <div>
            <p className="text-xs enterprise-text-muted uppercase tracking-wider font-medium">Plano ativo</p>
            <p className="text-base font-semibold enterprise-text-primary mt-0.5">
              {data.plan ? data.plan.toUpperCase() : 'Não configurado'}
            </p>
          </div>
          <div className="w-px h-8 bg-[var(--border-default)]" />
          <div>
            <p className="text-xs enterprise-text-muted uppercase tracking-wider font-medium">Status</p>
            <div className="mt-0.5">
              <EnterpriseStatusBadge label={normalizedStatus} tone={normalizedStatusTone} />
            </div>
          </div>
          {data.nextBilling && (
            <>
              <div className="w-px h-8 bg-[var(--border-default)]" />
              <div>
                <p className="text-xs enterprise-text-muted uppercase tracking-wider font-medium">Próxima cobrança</p>
                <p className="text-sm font-mono tabular-nums enterprise-text-primary mt-0.5">
                  {new Date(data.nextBilling).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </>
          )}
          {data.monthlyValue > 0 && (
            <>
              <div className="w-px h-8 bg-[var(--border-default)]" />
              <div>
                <p className="text-xs enterprise-text-muted uppercase tracking-wider font-medium">Valor mensal</p>
                <p className="text-sm font-mono font-semibold tabular-nums enterprise-text-primary mt-0.5">
                  {currency.format(data.monthlyValue)}
                </p>
              </div>
            </>
          )}
        </div>
        {canManageBilling && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onManageSubscription}
              disabled={isProcessing}
              className="enterprise-button-secondary text-xs font-medium px-3 inline-flex items-center gap-1.5"
            >
              {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
              Gerenciar assinatura
            </button>
            <button
              type="button"
              onClick={onChangePlan}
              disabled={isProcessing}
              className="enterprise-button-secondary text-xs font-medium px-3 inline-flex items-center gap-1.5"
            >
              <RefreshCw size={12} />
              Alterar plano
            </button>
          </div>
        )}
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-12 gap-3">
        {/* Left: Subscription details */}
        <div className="col-span-12 lg:col-span-8 space-y-3">
          {/* Detail rows */}
          <div className="enterprise-card-surface rounded overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[var(--border-default)] bg-[var(--bg-surface-2)]">
              <span className="text-xs font-medium enterprise-text-secondary uppercase tracking-wider">Detalhes da Assinatura</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {[
                  { label: 'Plano', value: data.plan ? data.plan.toUpperCase() : '—', mono: true },
                  { label: 'Status', value: <EnterpriseStatusBadge label={normalizedStatus} tone={normalizedStatusTone} />, mono: false },
                  { label: 'Valor mensal', value: currency.format(data.monthlyValue), mono: true },
                  { label: 'MRR', value: currency.format(data.mrr), mono: true },
                  {
                    label: 'Próxima cobrança',
                    value: data.nextBilling ? new Date(data.nextBilling).toLocaleDateString('pt-BR') : '—',
                    mono: true,
                  },
                  { label: 'Ciclo de cobrança', value: data.nextBilling ? 'Mensal' : '—', mono: false },
                  { label: 'Organização', value: orgName ?? '—', mono: false },
                ].map(({ label, value, mono }, idx) => (
                  <tr key={label} className={idx % 2 === 0 ? 'bg-[var(--bg-surface-1)]' : 'bg-[var(--bg-surface)]'}>
                    <td className="px-4 py-2.5 text-xs font-medium enterprise-text-secondary w-44 border-r border-[var(--border-default)]">
                      {label}
                    </td>
                    <td className={`px-4 py-2.5 text-xs enterprise-text-primary ${mono ? 'font-mono tabular-nums' : ''}`}>
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Plan features included */}
          {planDef && (
            <div className="enterprise-card-surface rounded overflow-hidden">
              <div className="px-4 py-2.5 border-b border-[var(--border-default)] bg-[var(--bg-surface-2)]">
                <span className="text-xs font-medium enterprise-text-secondary uppercase tracking-wider">Recursos incluídos no plano {planDef.name}</span>
              </div>
              <div className="px-4 py-3 grid grid-cols-2 gap-x-6 gap-y-1.5">
                {planDef.features.map((feat) => (
                  <div key={feat.label} className="flex items-center gap-2">
                    <CheckCircle2 size={12} className={feat.included ? 'text-[var(--brand-primary)] shrink-0' : 'text-[var(--text-muted)] shrink-0'} />
                    <span className={`text-xs ${feat.included ? 'enterprise-text-primary' : 'enterprise-text-muted line-through'}`}>{feat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: KPI tiles + actions */}
        <div className="col-span-12 lg:col-span-4 space-y-3">
          {/* KPI tiles */}
          <div className="enterprise-card-surface rounded overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[var(--border-default)] bg-[var(--bg-surface-2)]">
              <span className="text-xs font-medium enterprise-text-secondary uppercase tracking-wider">Indicadores Financeiros</span>
            </div>
            <div className="divide-y divide-[var(--border-default)]">
              <KpiRow icon={CreditCard} label="Receita mensal recorrente" value={currency.format(data.mrr)} />
              <KpiRow icon={ReceiptText} label="Ticket mensal" value={currency.format(data.monthlyValue)} />
              <KpiRow icon={CalendarClock} label="Ciclo de cobrança" value={data.nextBilling ? 'Mensal ativo' : '—'} />
            </div>
          </div>

          {/* Security block */}
          <div className="enterprise-card-surface rounded px-4 py-3 flex items-start gap-3">
            <ShieldCheck size={15} className="shrink-0 mt-0.5 text-[var(--brand-primary)]" />
            <div>
              <p className="text-xs font-medium enterprise-text-primary">Pagamento seguro</p>
              <p className="text-xs enterprise-text-muted mt-0.5 leading-relaxed">
                Cobranças processadas via Stripe. Dados de cartão nunca armazenados em nossos servidores.
              </p>
            </div>
          </div>

          {canManageBilling && (
            <div className="enterprise-card-surface rounded px-4 py-3 space-y-2">
              <p className="text-xs font-medium enterprise-text-secondary uppercase tracking-wider">Ações</p>
              <button
                type="button"
                onClick={onManageSubscription}
                disabled={isProcessing}
                className="w-full enterprise-button-secondary text-xs font-medium px-3 inline-flex items-center gap-1.5 justify-center"
              >
                {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                Portal de pagamento
              </button>
              <button
                type="button"
                onClick={onChangePlan}
                disabled={isProcessing}
                className="w-full enterprise-button-secondary text-xs font-medium px-3 inline-flex items-center gap-1.5 justify-center"
              >
                <RefreshCw size={12} />
                Alterar plano
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PLAN CHANGE MODAL
// ============================================================================

const PlanChangeModal: React.FC<{
  selectedPlan: string;
  isProcessing: boolean;
  currency: Intl.NumberFormat;
  onSelectPlan: (id: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}> = ({ selectedPlan, isProcessing, currency, onSelectPlan, onConfirm, onClose }) => (
  <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
    <div className="enterprise-card-surface w-full max-w-lg rounded overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-default)] bg-[var(--bg-surface-2)]">
        <div className="flex items-center gap-2">
          <CreditCard size={14} className="enterprise-text-secondary" />
          <h3 className="text-sm font-semibold enterprise-text-primary">Alterar Plano</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="enterprise-button-secondary inline-flex h-7 w-7 items-center justify-center p-0 rounded"
          title="Fechar"
        >
          <X size={13} />
        </button>
      </div>

      {/* Plan list */}
      <div className="px-5 py-4 space-y-2">
        {PLAN_DEFINITIONS.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan === plan.id;
          return (
            <label
              key={plan.id}
              className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 border transition-colors ${
                isSelected
                  ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/5'
                  : 'border-[var(--border-default)] enterprise-card-surface hover:bg-[var(--bg-surface-2)]'
              }`}
            >
              <div className={`shrink-0 w-7 h-7 rounded flex items-center justify-center ${
                isSelected ? 'bg-[var(--brand-primary)] text-[var(--text-inverse)]' : 'bg-[var(--bg-surface-2)] enterprise-text-secondary'
              }`}>
                <Icon size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-xs font-semibold enterprise-text-primary">{plan.name}</span>
                <span className="block text-xs enterprise-text-muted">
                  {plan.value > 0 ? `${currency.format(plan.value)}/mês` : plan.id === 'enterprise' ? 'Sob consulta' : 'Grátis'}
                </span>
              </div>
              <input
                type="radio"
                name="billing-plan"
                value={plan.id}
                checked={isSelected}
                onChange={(e) => onSelectPlan(e.target.value)}
                className="h-3.5 w-3.5 shrink-0"
              />
            </label>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-5 py-3.5 border-t border-[var(--border-default)] bg-[var(--bg-surface-2)] flex items-center justify-between gap-3">
        <p className="text-xs enterprise-text-muted">
          Você será redirecionado ao portal de pagamento Stripe.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isProcessing}
            className="enterprise-button-secondary px-4 text-xs font-medium"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isProcessing}
            className="enterprise-button-primary px-5 text-xs font-semibold inline-flex items-center gap-1.5"
          >
            {isProcessing && <Loader2 size={12} className="animate-spin" />}
            {isProcessing ? 'Processando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ============================================================================
// SHARED SUB-COMPONENTS
// ============================================================================

const KpiRow: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between px-4 py-2.5">
    <div className="flex items-center gap-2">
      <Icon size={12} className="enterprise-text-muted shrink-0" />
      <span className="text-xs enterprise-text-secondary">{label}</span>
    </div>
    <span className="text-xs font-mono font-semibold tabular-nums enterprise-text-primary">{value}</span>
  </div>
);
